/**
 * Period Selector Component
 *
 * Allows users to select commission periods (YYYY-MM format).
 * Displays period as month name + year for better UX.
 */

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/frontend/components/ui/select";
import { CalendarIcon } from "lucide-react";

interface PeriodSelectorProps {
  value?: string; // YYYY-MM format
  onChange: (period: string) => void;
  disabled?: boolean;
}

/**
 * Generate list of available periods (last 12 months)
 */
function generatePeriods(): Array<{ value: string; label: string }> {
  const periods: Array<{ value: string; label: string }> = [];
  const now = new Date();

  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    const value = `${year}-${String(month).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

    periods.push({ value, label });
  }

  return periods;
}

export function PeriodSelector({ value, onChange, disabled }: PeriodSelectorProps) {
  const periods = generatePeriods();

  // Default to current period if no value
  const currentValue = value || periods[0].value;

  return (
    <div className="flex items-center gap-2">
      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
      <Select value={currentValue} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent>
          {periods.map((period) => (
            <SelectItem key={period.value} value={period.value}>
              {period.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
